import { ColorNeutralGrey5, Spacing16, Spacing4 } from 'autogen/design-tokens/tokens';
import type { ProductGwpOfferViewModel, ProductInformationViewModel } from 'autogen/swagger/all';
import {
    GwpFromValue,
    GwpValue,
    OpenGWPModal,
    OpenGWPModalPlural
} from 'autogen/translation-keys/trans-website-product-gwpoffer';
import ArticleCard from 'components/ArticleCard/ArticleCard';
import type { IArticleCard } from 'components/ArticleCard/shared/types';
import { useQuickView } from 'components/quickView';
import QuickView from 'components/quickView/quickView';
import { fetchHTML } from 'modules/helpers/fetch';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toHtmlContent, format } from 'modules/helpers/strings';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import { customScrollbar } from 'modules/helpers/mixins';
import toast from 'components/Toast';

const ImageContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 80px;
    max-width: 80px;
    padding: ${Spacing4};

    &:after {
        content: '';
        ${imageOverlayStyle}
    }
`;

const Image = styled.img`
    max-width: 100%;
    max-height: 80px;
`;

const QuickViewContainer = styled.div`
    padding-left: ${Spacing16};
    padding-right: ${Spacing16};

    ${customScrollbar};
`;

const StyledArticleCard = styled(ArticleCard)`
    border: 1px solid ${ColorNeutralGrey5};
    cursor: pointer;
`;

export default function GWPOffer({
    gwpOfferModel,
    gwpOfferDetailsLinkAction,
    ...restProps
}: {
    gwpOfferModel: ProductGwpOfferViewModel;
    gwpOfferDetailsLinkAction: ProductInformationViewModel['gwpOfferDetailsLinkAction'];
}): JSX.Element {
    const { primaryImage, productId, marketingValue, hasMultipleGwpProducts, title } = gwpOfferModel;
    const [quickViewContent, setQuickViewContent] = useState<string | null>(null);
    const { t } = useTranslation();
    const { toggle } = useQuickView('GWPOffer');

    const overlineText = hasMultipleGwpProducts
        ? format(t(GwpFromValue))(marketingValue)
        : format(t(GwpValue))(marketingValue);

    const onClickHandler = () => {
        fetchHTML(`${gwpOfferDetailsLinkAction.url}`)
            .then((html) => {
                setQuickViewContent(html);
                toggle(true);
            })
            .catch(() => {
                toast.error(t(GeneralExceptionError));
            });

        UAEventTracking({
            eventCategory: 'productPage',
            eventAction: 'openModal',
            eventLabel: 'GWP Offer'
        });
    };

    const articleCardArgs: IArticleCard = {
        textBox: {
            title: title || '',
            overline: marketingValue ? overlineText : undefined,
            action: {
                buttonText: hasMultipleGwpProducts ? t(OpenGWPModalPlural) : t(OpenGWPModal)
            }
        },
        customTheme: { colorTheme: 'neutral', layoutTheme: 'horizontalSmall', disableEllipsis: true }
    };

    return (
        <>
            <StyledArticleCard onClick={onClickHandler} {...articleCardArgs} {...restProps}>
                {primaryImage && (
                    <ImageContainer>
                        <Image src={primaryImage.url} alt="" />
                    </ImageContainer>
                )}
            </StyledArticleCard>

            <QuickView quickViewId="GWPOffer" position="right">
                <QuickViewContainer>{toHtmlContent(quickViewContent)}</QuickViewContainer>
            </QuickView>
        </>
    );
}
